/**
 * Created by Ivan on 16.03.2026
 */

class WithoutBgComponent extends ScriptComponent {
    onCreate() {
        this.view = PrefabBuilder.resolveProperty(this.options.view, this.context);
        this.styles = this.options.styles;
        this.contentName = this.options.contentName || "content";

        // Defaults to true to preserve the prior behaviour. Opt out by
        // passing `styles.withShadow: false` from the prefab JSON.
        this.view.withShadow = this.styles.withShadow !== false;
    }

    resolveStyle(value) {
        if (typeof value === "string" && value.startsWith("#")) {
            const [name, method] = value.slice(1).split(".");
            const src = this.context.propertySources && this.context.propertySources[name];
            if (src && typeof src[method] === "function") {
                return src[method]();
            }

            // an unresolved reference used to fall through to `return value` - a non-empty string,
            // so a failed lookup silently read as `true` and enabled whatever the style gated
            cleverapps.throwAsync(`Cannot resolve style "${value}" - available sources: ${Object.keys(this.context.propertySources || {})}`);
            return undefined;
        }
        return value;
    }

    onAttach() {
        const node = this.node;
        const styles = this.styles;
        const view = this.view;

        const titleText = PrefabBuilder.localize(this.resolveStyle(styles.titleText));
        if (titleText) {
            const titleBg = cleverapps.UI.createScale9Sprite(
                bundles.windows.frames.window_title_bg_secondary_png,
                styles.titleScale9Type && cleverapps.UI.Scale9Rect[styles.titleScale9Type]
            );
            this._titleText = cleverapps.UI.generateOnlyText(titleText, cleverapps.styles.FONTS.NOBG_WINDOW_TITLE_TEXT);
            titleBg.addChild(this._titleText);
            titleBg.setName("title");
            node.addChild(titleBg);
        }

        if (this.resolveStyle(styles.tapToContinue)) {
            this._tapToContinue = cleverapps.UI.generateOnlyText(Messages.get("Window.TapToContinue"), cleverapps.styles.FONTS.WHITE_TEXT);
            this._tapToContinue.setName("tapToContinue");
            view.addChild(this._tapToContinue);

            cleverapps.UI.onClick(view, () => {
                ComponentMap.get(view, WindowComponent).closeWindow();
            }, { interactiveScale: false });
        } else if (!styles.noCloseButton) {
            const closeButton = new CloseRoundButton({
                onClicked: () => ComponentMap.get(view, WindowComponent).closeWindow()
            });
            const closeParent = styles.closeButton && styles.closeButton.parent === "window"
                ? view
                : ComponentUtils.findChildByName(node, "title") || node;
            closeButton.setName("closeButton");
            closeParent.addChild(closeButton);
        }

        this.onResize();
    }

    onResize() {
        const sceneSize = cleverapps.resolution.getSceneSize();
        const styles = this.styles;
        const node = this.node;
        const defaults = WithoutBgComponent.DEFAULTS;

        this.view.setContentSize(sceneSize.width, sceneSize.height);
        this.view.setPosition(sceneSize.width / 2, sceneSize.height / 2);

        // a layout content is re-measured and its rows centered; a plain node is taken at the size
        // it was given, so a window can reserve a frame its children are free to overflow
        const content = ComponentUtils.findChildByName(node, this.contentName);
        if (content.reshape) {
            content.reshape();
            content.children.forEach((child) => {
                child.setPositionX(content.width / 2 - child.width * (0.5 - child.anchorX));
            });
        }

        const contentPadding = styles.contentPadding || {};
        const padTop = PrefabBuilder.resolveSize(contentPadding.top || 0);
        const padBottom = PrefabBuilder.resolveSize(contentPadding.bottom || 0);
        const padX = PrefabBuilder.resolveSize(contentPadding.x || 0);
        node.setContentSize(content.width + padX * 2, content.height + padTop + padBottom);

        const title = ComponentUtils.findChildByName(node, "title");
        if (title) {
            const titleHeight = PrefabBuilder.resolveSize(styles.titleHeight || defaults.titleHeight);
            const titleTextPadding = PrefabBuilder.resolveSize(styles.titleTextPadding || defaults.titleTextPadding);

            let titleWidth;
            if (styles.titleFitText && this._titleText) {
                this._titleText.fitTo(undefined, titleHeight * 0.8);
                titleWidth = Math.max(
                    this._titleText.width + 2 * titleTextPadding,
                    PrefabBuilder.resolveSize(styles.titleMinWidth || defaults.minTitleWidth)
                );
            } else if (styles.titleWidth) {
                titleWidth = PrefabBuilder.resolveSize(styles.titleWidth);
            } else {
                titleWidth = Math.max(content.width, PrefabBuilder.resolveSize(defaults.minTitleWidth));
            }

            title.setContentSize(titleWidth, titleHeight);

            if (this._titleText) {
                if (!styles.titleFitText) {
                    this._titleText.fitTo(titleWidth - titleTextPadding, titleHeight * 0.8);
                }
                this._titleText.setPosition(titleWidth / 2, titleHeight / 2 + PrefabBuilder.resolveSize(styles.titleTextDy || defaults.titleTextDy));
            }
        }

        const titleTotalHeight = title ? title.height : 0;
        const scale = Math.min(1, sceneSize.width / node.width, sceneSize.height / (node.height + titleTotalHeight));
        node.setScale(scale);

        node.setPosition(this.view.width / 2, this.view.height / 2 - (title ? titleTotalHeight * scale / 2 : 0));
        content.setPosition(node.width / 2, (node.height - padTop + padBottom) / 2);

        if (title) {
            const titleDy = Array.isArray(styles.titleDy) ? styles.titleDy[cleverapps.resolution.mode] : (styles.titleDy || 0);
            title.setPosition(node.width / 2, node.height + PrefabBuilder.resolveSize(titleDy) + title.height / 2);
        }

        if (this._tapToContinue) {
            this._tapToContinue.setPosition(this.view.width / 2, PrefabBuilder.resolveSize(styles.tapToContinueY || defaults.tapToContinueY));
        }

        if (styles.closeButton) {
            const closeButton = ComponentUtils.findChildByName(this.view, "closeButton");
            if (closeButton) {
                if (styles.closeButton.position) {
                    closeButton.setAlignment(styles.closeButton.position);
                }
                if (styles.closeButton.scale !== undefined) {
                    const closeScale = Array.isArray(styles.closeButton.scale)
                        ? styles.closeButton.scale[cleverapps.resolution.mode]
                        : styles.closeButton.scale;
                    closeButton.setScale(closeScale);
                }
            }
        }
    }
}

WithoutBgComponent.DEFAULTS = {
    minTitleWidth: 600,
    titleHeight: 110,
    titleTextPadding: 220,
    titleTextDy: 22,
    tapToContinueY: 40
};

Prefab.registerComponent("WithoutBgComponent", WithoutBgComponent);
